import { useApolloClient, useLazyQuery } from '@apollo/client';
import { IonText } from '@ionic/react';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import DateStamp from '@components/dates/DateStamp';
import HandleQuery from '@components/HandleQuery';
import Nbsp from '@components/Nbsp';
import styles from '@components/NewClientsList.module.css';
import Table, { TableHeader } from '@components/Table';
import HugeText from '@components/text/HugeText';
import Link from '@components/text/Link';
import useSubscribeToJourneys from '@hooks/subscriptions/useSubscribeToJourneys';
import useMyExpert from '@hooks/useMyExpert';
import useNodesFromConnection from '@hooks/useNodesFromConnection';
import {
  ClientAccessTypeEnum,
  ClientRoleRelationshipEnum,
  ExpertJourneysSortByEnum,
  GetExpertJourneysDocument,
  GetExpertJourneysQuery,
  GetExpertJourneysQueryVariables,
  SortDirectionEnum
} from '@typing/Generated';
import { journeyRoute } from '@utils/routeUtils';

type Props = {
  expertId: string;
};

const NewClientsList = ({ expertId }: Props) => {
  const apolloClient = useApolloClient();
  const myExpert = useMyExpert();

  const [getExpertJourneys, getExpertJourneysQuery] = useLazyQuery<
    GetExpertJourneysQuery,
    GetExpertJourneysQueryVariables
  >(GetExpertJourneysDocument);

  useSubscribeToJourneys(() => {
    // This kicks out all of the expertJourneys queries so we will re-make them. If you're flipping between filters
    // then the queries related to them will be cached, but if a journey is changed then we need to redo those queries.
    apolloClient.cache.evict({ fieldName: 'expertJourneys', id: 'ROOT_QUERY' });
    getExpertJourneysQuery.refetch();
  });

  useEffect(() => {
    if (expertId) {
      getExpertJourneys({
        variables: {
          expertId,
          expertUnopened: true,
          sortBy: ExpertJourneysSortByEnum.created_at,
          sortDirection: SortDirectionEnum.descending
        }
      });
    }
  }, [expertId, getExpertJourneys]);

  useEffect(() => {
    if (!getExpertJourneysQuery.loading && getExpertJourneysQuery.data?.expertJourneys.pageInfo.hasNextPage) {
      getExpertJourneysQuery.fetchMore({
        variables: {
          expertId,
          expertUnopened: true,
          sortBy: ExpertJourneysSortByEnum.created_at,
          sortDirection: SortDirectionEnum.descending
        }
      });
    }
  }, [expertId, getExpertJourneysQuery]);

  const journeys = useNodesFromConnection(getExpertJourneysQuery.data?.expertJourneys);

  const tableHeaders: TableHeader<ExpertJourneysSortByEnum>[] = [
    { messageKey: 'pages.expert.dashboard.newClients.tableHeadings.client' },
    { messageKey: 'pages.expert.dashboard.newClients.tableHeadings.employer' },
    { messageKey: 'pages.expert.dashboard.newClients.tableHeadings.lovedOne' },
    { messageKey: 'pages.expert.dashboard.newClients.tableHeadings.joined' }
  ];

  return (
    <HandleQuery query={getExpertJourneysQuery}>
      {data => (
        <>
          {journeys.length ? (
            <Table<ExpertJourneysSortByEnum>
              fetchMore={getExpertJourneysQuery.fetchMore}
              headerKey="pages.expert.dashboard.newClients.tableHeadings"
              headers={tableHeaders}
              pageInfo={data.expertJourneys.pageInfo}
              skipScroll
            >
              {journeys.map(journey => {
                const ownerRole = journey.owner.roles.find(role => role.journey.id === journey.id);
                return (
                  <tr key={journey.id}>
                    <td>
                      {journey.owner.lastName}
                      <FormattedMessage id="dictionary.punctuation.comma" />
                      <Nbsp />
                      {journey.owner.firstName}
                    </td>
                    <td>
                      {journey.owner.accessType === ClientAccessTypeEnum.EMPLOYER_SPONSORED ? (
                        <>{journey.owner.employer?.name}</>
                      ) : (
                        <FormattedMessage id="dictionary.selfPaid" />
                      )}
                    </td>
                    <td>
                      <Link
                        className={styles.bigText}
                        data-test="journey-route"
                        routerLink={journeyRoute({ journeyId: journey.id, linkAfterJourney: '', myExpert })}
                      >
                        {journey.careProfile.name ? (
                          <>{journey.careProfile.name}</>
                        ) : (
                          <FormattedMessage id="dictionary.unknown" />
                        )}
                      </Link>
                      <div className={styles.subHead}>
                        {ownerRole?.relationship?.relationshipOption &&
                          ownerRole?.relationship?.relationshipOption !== ClientRoleRelationshipEnum.other && (
                            <>
                              <FormattedMessage
                                id={`models.clientRole.relationship.${ownerRole?.relationship?.relationshipOption}`}
                              />
                              {journey.careProfile.age && (
                                <>
                                  <FormattedMessage id="dictionary.punctuation.comma" />
                                  <Nbsp />
                                </>
                              )}
                            </>
                          )}
                        {ownerRole?.relationship?.other && (
                          <>
                            {ownerRole.relationship.other}
                            {journey.careProfile.age && (
                              <>
                                <FormattedMessage id="dictionary.punctuation.comma" />
                                <Nbsp />
                              </>
                            )}
                          </>
                        )}
                        {journey.careProfile.age && (
                          <FormattedMessage id={`models.careProfile.age.${journey.careProfile.age}`} />
                        )}
                      </div>
                    </td>
                    <td>
                      <DateStamp showWeekday={false} value={journey.createdAt} />
                    </td>
                  </tr>
                );
              })}
            </Table>
          ) : (
            <div className={styles.noNewClients}>
              <HugeText>
                <FormattedMessage id="pages.expert.dashboard.newClients.noNewClients" />
              </HugeText>
              <div className={styles.spacing}>
                <IonText>
                  <FormattedMessage id="pages.expert.dashboard.newClients.viewedThemAll" />
                </IonText>
              </div>
            </div>
          )}
        </>
      )}
    </HandleQuery>
  );
};

export default NewClientsList;
